/**
 * Shared meeting utility functions.
 *
 * Shared utilities used by the meeting slideout, calendar pages, and
 * dashboard meeting rows.
 */

import {
  DEFAULT_TIME_FORMAT,
  formatTime,
  formatWithSafeTimeZone,
  type TimeFormat,
  timeParts,
} from '@/lib/time';
import type { CalendarEventGuest } from '@/pages/calendar/utils/eventMapper';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

// ============================================================================
// Time Formatting
// ============================================================================
//
// These are thin domain wrappers over `@/lib/time` — the org-format decision,
// midnight-safe hour cycle, invalid-timezone fallback, and ":00" stripping all
// live in `formatTime`/`formatWithSafeTimeZone`. Wrappers only pin the
// meeting-surface label shape (locale, date parts). Don't add formatting
// branches here; extend `@/lib/time` instead.

/**
 * Format event time as "9:00 AM" or "9 AM" (omits :00 for on-the-hour in
 * 12-hour format; 24-hour output keeps ":00", e.g. "14:00").
 *
 * @param timeString - ISO 8601 date-time string
 * @param timeZone - Optional IANA timezone (e.g. "America/New_York"). Falls back to browser local time.
 * @param format - 24-hour or 12-hour clock; defaults to {@link DEFAULT_TIME_FORMAT}.
 */
export function formatMeetingTime(
  timeString: string,
  timeZone?: string,
  format: TimeFormat = DEFAULT_TIME_FORMAT
): string {
  return formatTime(timeString, format, {
    timeZone,
    locale: 'en-US',
    omitZeroMinutes: true,
  });
}

/**
 * Format event date and time as "Jul 15, 9:00 AM" (omits :00 for on-the-hour
 * in 12-hour format; 24-hour output keeps ":00", e.g. "Jul 15, 14:00").
 *
 * Unlike {@link formatMeetingTime}, this includes the date so occurrences on
 * different days that share a start time remain distinguishable.
 *
 * @param timeString - ISO 8601 date-time string
 * @param timeZone - Optional IANA timezone (e.g. "America/New_York"). Falls back to browser local time.
 * @param format - 24-hour or 12-hour clock; defaults to {@link DEFAULT_TIME_FORMAT}.
 */
export function formatEventDateTime(
  timeString: string,
  timeZone?: string,
  format: TimeFormat = DEFAULT_TIME_FORMAT
): string {
  return formatTime(timeString, format, {
    timeZone,
    locale: 'en-US',
    omitZeroMinutes: true,
    dateParts: { month: 'short', day: 'numeric' },
  });
}

/**
 * Y-M-D parts of an instant in the given timezone (browser-local on fallback),
 * as an ISO "YYYY-MM-DD" string suitable for same-day comparison.
 */
function isoDateParts(date: Date, timeZone?: string): string {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  try {
    return new Intl.DateTimeFormat('en-CA', {
      ...options,
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-CA', options).format(date);
  }
}

/**
 * Whether the ISO timestamp falls on the current calendar day in `timeZone`
 * (browser-local when the timezone is missing/invalid). Returns false for an
 * unparseable timestamp.
 */
export function isMeetingToday(timeString: string, timeZone?: string): boolean {
  const date = new Date(timeString);
  if (isNaN(date.getTime())) {
    return false;
  }
  return isoDateParts(date, timeZone) === isoDateParts(new Date(), timeZone);
}

/**
 * Format the event's date as "Wed, Jul 15" — omitting the year when it's the
 * current year and including it ("Wed, Jul 15, 2027") otherwise. Returns '' for
 * an unparseable timestamp. Pair with {@link isMeetingToday} to show the date
 * only when a meeting isn't today.
 */
export function formatMeetingDate(
  timeString: string,
  timeZone?: string
): string {
  const date = new Date(timeString);
  if (isNaN(date.getTime())) {
    return '';
  }
  const sameYear =
    isoDateParts(date, timeZone).slice(0, 4) ===
    isoDateParts(new Date(), timeZone).slice(0, 4);
  return formatWithSafeTimeZone(
    date,
    {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    },
    { locale: 'en-US', timeZone }
  );
}

/** Maximum duration in minutes to show duration label (2 hours) */
const MAX_DURATION_DISPLAY_MINUTES = 120;

/**
 * Get meeting duration in minutes, or null if >= 2 hours.
 */
export function getMeetingDurationMinutes(
  startTime: string,
  endTime: string
): number | null {
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  if (isNaN(start) || isNaN(end)) {
    return null;
  }
  const durationMs = end - start;

  if (durationMs >= MAX_DURATION_DISPLAY_MINUTES * 60 * 1000) {
    return null;
  }

  return Math.round(durationMs / (1000 * 60));
}

export {
  getMeetingIconSources,
  getMeetingPlatformLabel,
  getVideoPlatform,
  looksLikeUrl,
  type MeetingIconSource,
  platformIcons,
  platformNames,
  type VideoPlatform,
} from './meetingPlatforms';

// ============================================================================
// Meeting Join Logic
// ============================================================================

/** Minutes before meeting start within which a meeting counts as imminent
 *  (drives Join-button prominence, not visibility). */
const JOIN_WINDOW_MINUTES = 15;

/**
 * Check if a meeting is imminent or currently happening (drives Join-button
 * prominence — primary styling — not whether the button is shown at all).
 * Returns true if:
 * - Meeting starts within JOIN_WINDOW_MINUTES, OR
 * - Meeting has started but not yet ended
 */
export function isMeetingImminent(startTime: string, endTime: string): boolean {
  const now = Date.now();
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();

  const windowStart = start - JOIN_WINDOW_MINUTES * 60 * 1000;

  return now >= windowStart && now < end;
}

/**
 * Get the next timestamp at which the join button state will change.
 * Use with `useRefreshAt` to trigger a re-render at the right moment.
 *
 * - Before join window: returns window start (join will appear)
 * - During join window / meeting: returns end time (join will disappear)
 * - After meeting: returns null (no further state change)
 */
export function getNextJoinStateChange(
  startTime: string,
  endTime: string
): number | null {
  const now = Date.now();
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const windowStart = start - JOIN_WINDOW_MINUTES * 60 * 1000;

  if (now < windowStart) {
    return windowStart;
  }
  if (now < end) {
    return end;
  }
  return null;
}

// ============================================================================
// Meeting Date Range Formatting
// ============================================================================

/**
 * Format a meeting start/end time pair as a readable date·time range with duration.
 * Returns null when startTime is missing or invalid.
 * Separator defaults to ' · ' to match the overview enrichment style.
 */
export function formatMeetingDateRange(
  startTime: string | null,
  endTime: string | null,
  separator = ' · ',
  format: TimeFormat = DEFAULT_TIME_FORMAT
): string | null {
  if (!startTime) {
    return null;
  }
  const start = new Date(startTime);
  if (isNaN(start.getTime())) {
    return null;
  }
  const datePart = start.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const startTimePart = start.toLocaleTimeString(undefined, timeParts(format));
  if (!endTime) {
    return `${datePart}${separator}${startTimePart}`;
  }
  const end = new Date(endTime);
  if (isNaN(end.getTime())) {
    return `${datePart}${separator}${startTimePart}`;
  }
  const endTimePart = end.toLocaleTimeString(undefined, {
    ...timeParts(format),
    timeZoneName: 'short',
  });
  const durationMins = Math.round((end.getTime() - start.getTime()) / 60_000);
  const parts = [datePart, `${startTimePart} – ${endTimePart}`];
  if (durationMins > 0) {
    parts.push(`${durationMins} mins`);
  }
  return parts.join(separator);
}

// ============================================================================
// Guest Display
// ============================================================================

/**
 * Get display name for a guest.
 * Prefers name, falls back to email (before @).
 */
export function getGuestDisplayName(guest: CalendarEventGuest): string {
  if (guest.name) {
    return guest.name;
  }
  const atIndex = guest.email.indexOf('@');
  return atIndex > 0 ? guest.email.slice(0, atIndex) : guest.email;
}

/**
 * Detect meetings where every non-organizer guest has declined.
 *
 * From the calendar owner's perspective, a meeting can't realistically happen
 * when every counter-party has declined — even though Google's authoritative
 * meeting status remains "confirmed" because the organizer hasn't cancelled
 * it. Used by the dashboard and the calendar to de-emphasize such meetings
 * (visual signal that they won't really happen) without hiding them, so the
 * user can find and cancel them at the source.
 *
 * Returns false when there are no non-organizer guests (e.g., a solo focus
 * block or a meeting whose only invitee is the organizer themselves) — those
 * render normally.
 */
export function allNonOrganizerGuestsDeclined(
  guests: CalendarEventGuest[] | undefined
): boolean {
  if (!guests || guests.length === 0) {
    return false;
  }
  const nonOrganizerGuests = guests.filter(g => !g.is_organizer);
  if (nonOrganizerGuests.length === 0) {
    return false;
  }
  return nonOrganizerGuests.every(g => g.response_status === 'declined');
}

/**
 * Tailwind className applied to the title text of a meeting where every
 * non-organizer guest has declined. Single source of truth — used by every
 * surface that renders a meeting title (dashboard `MeetingRow`, calendar
 * `WeekDayColumn` / `MonthEventChip` / `MonthInlineEventRow` / search bar).
 *
 * Compose with `cx()` and gate on {@link allNonOrganizerGuestsDeclined}:
 *
 *   className={cx('...', isAllDeclined && DECLINED_TITLE_CLASSNAME)}
 *
 * Change the visual treatment here and every surface updates together.
 */
export const DECLINED_TITLE_CLASSNAME = 'line-through opacity-70';

// ============================================================================
// Meeting Origin
// ============================================================================

/**
 * Whether a meeting was synthesized from notes for an ad hoc call that was never on a
 * calendar (`ahoy_origin === 'ad_hoc'`). Missing or any other origin returns false. Mirrors
 * `isMeetingRecurring` and the `getPropertyValue<string>(...) === '<option>'` shape already
 * used for `ahoy_origin` in scheduler-booking-section.tsx.
 */
export function isMeetingAdHoc(meeting: CrmObject): boolean {
  return getPropertyValue<string>(meeting.properties?.ahoy_origin) === 'ad_hoc';
}
